import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiService } from '@/services/api'
import { FolderPermission, PermissionLevel } from '@/types'

interface PermissionsState {
  byFolderId: Record<number, FolderPermission[]>
  isLoading: Record<number, boolean>
  error: string | null
}

const initialState: PermissionsState = {
  byFolderId: {},
  isLoading: {},
  error: null,
}

export const fetchFolderPermissions = createAsyncThunk(
  'permissions/fetchFolderPermissions',
  async (folderId: number) => {
    const permissions = await apiService.getFolderPermissions(folderId)
    return { folderId, permissions }
  }
)

export const grantPermission = createAsyncThunk(
  'permissions/grantPermission',
  async ({
    folderId,
    userId,
    groupId,
    permission,
  }: {
    folderId: number
    userId?: number
    groupId?: number
    permission: PermissionLevel
  }) => {
    const newPerm = await apiService.grantFolderPermission(folderId, {
      user_id: userId,
      group_id: groupId,
      permission,
    })
    return { folderId, permission: newPerm }
  }
)

export const revokePermission = createAsyncThunk(
  'permissions/revokePermission',
  async ({
    folderId,
    userId,
    groupId,
  }: {
    folderId: number
    userId?: number
    groupId?: number
  }) => {
    await apiService.revokeFolderPermission(folderId, { user_id: userId, group_id: groupId })
    return { folderId, userId, groupId }
  }
)

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolderPermissions.pending, (state, action) => {
        state.isLoading[action.meta.arg] = true
        state.error = null
      })
      .addCase(fetchFolderPermissions.fulfilled, (state, action) => {
        const { folderId, permissions } = action.payload
        state.isLoading[folderId] = false
        state.byFolderId[folderId] = permissions
      })
      .addCase(fetchFolderPermissions.rejected, (state, action) => {
        state.isLoading[action.meta.arg] = false
        state.error = action.error.message || 'Failed to load permissions'
      })
      .addCase(grantPermission.fulfilled, (state, action) => {
        const { folderId, permission } = action.payload
        const existing = state.byFolderId[folderId] || []
        // Replace if already exists (upsert), otherwise add
        const idx = existing.findIndex(
          (p) =>
            (permission.user_id && p.user_id === permission.user_id) ||
            (permission.group_id && p.group_id === permission.group_id)
        )
        if (idx >= 0) {
          existing[idx] = permission
        } else {
          existing.push(permission)
        }
        state.byFolderId[folderId] = existing
      })
      .addCase(revokePermission.fulfilled, (state, action) => {
        const { folderId, userId, groupId } = action.payload
        if (state.byFolderId[folderId]) {
          state.byFolderId[folderId] = state.byFolderId[folderId].filter((p) => {
            if (userId && p.user_id === userId) return false
            if (groupId && p.group_id === groupId) return false
            return true
          })
        }
      })
  },
})

export default permissionsSlice.reducer
